import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotifications,
  updateNotificationStatus,
} from "../../slice/Matterslice";
import { fetchNotification } from "../../service/matter";
import { postHighlightedMatter } from "../../slice/MatterDetailsSlice";
import { useNavigate } from "react-router-dom";
import groupNotificationsByDate from "../../utils/groupNotificationsByDate";
import { Box } from "@mui/material";
import { Typography } from "antd";
import NotificationBlock from "../../components/Notification/NotificationBlock";
import Loader from "../../components/Loader/Loader";
import ErrorToast from "../../components/Notification/ErrorToast";
import { StyledTabs } from "./StyledTabs";
import { Virtuoso } from "react-virtuoso";
import { FooterComponent } from "../../components/Notification/NotificationFooter";
import TimeFilterDropdown from "../../components/TimeFilterDropdown";
import { formatToISO, getDateRange } from "../../utils/dateFormatting";
import { NoNotificationsIcon } from "../../assets/icons";
import { onNotificationClickFactory } from "../../utils/navigationUrl";

/* eslint-disable react/display-name */

const getVirtuosoFooter = (isLoading, moreNotifications) => () =>
  (
    <FooterComponent
      isLoading={isLoading}
      moreNotifications={moreNotifications}
    />
  );

const itemContent = (flatList, onNotificationClick) => (index, entry) => {
  if (entry.type === "group") {
    return (
      <div style={{ margin: "16px 0 8px" }}>
        <Typography.Text type="secondary">{entry.label}</Typography.Text>
      </div>
    );
  }

  return (
    <Box sx={{ paddingRight: 2 }}>
      <NotificationBlock
        key={entry.data.id}
        item={entry.data}
        index={index}
        isLast={index === flatList.length - 1}
        onClick={onNotificationClick}
      />
    </Box>
  );
};

export const NotificationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showNotification, setShowNotification] = useState(false);
  const [page, setPage] = useState(1);
  const [notificationError, setNotificationError] = useState([]);
  const [activeKey, setActiveKey] = useState("unread");
  const [timeFilter, setTimeFilter] = useState("1m");
  const [selectedCustomFilter, setSelectedCustomFilter] =
    useState("date_range");
  const [customDate, setCustomDate] = useState(null);
  const [customRange, setCustomRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [appliedRange, setAppliedRange] = useState({
    startDate: null,
    endDate: null,
  });

  const [apply, setApply] = useState(false);
  const [labelRange, setLabelRange] = useState({
    startDate: null,
    endDate: null,
    sideMenuSelection: "",
  });

  const [allCount, setAllCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isCustomCalendarOpen, setIsCustomCalendarOpen] = useState(false);
  const skipNextFetchRef = useRef(false);

  const { items: notificationItems, moreNotifications } = useSelector(
    (state) => state.matter.notifications
  );

  const status = useSelector((state) => state.matter.status);
  const notificationRefreshTrigger = useSelector((state) => state.websocket.notificationRefreshTrigger);

  const isLoading = status === "loading";

  const reloadPage = isLoading && page === 1;

  const isCustomFilter = timeFilter === "custom";

  const grouped = useMemo(
    () => groupNotificationsByDate(notificationItems),
    [notificationItems]
  );

  const flatList = useMemo(() => {
    const result = [];

    for (const [groupLabel, items] of Object.entries(grouped)) {
      result.push({ type: "group", label: groupLabel });

      for (const item of items) {
        result.push({ type: "item", data: item });
      }
    }
    return result;
  }, [grouped]);

  const firstGroupLabel = useMemo(
    () => (Object.keys(grouped).length > 0 ? Object.keys(grouped)[0] : ""),
    [grouped]
  );

  const flatListForRender = useMemo(() => {
    if (
      activeKey !== "unread" &&
      flatList.length > 0 &&
      flatList[0].type === "group"
    ) {
      return flatList.slice(1);
    }
    return flatList;
  }, [flatList, activeKey]);

  const buildNotificationPayload = useCallback(() => {
    const payload = {
      offset: (page - 1) * 20,
      limit: 20,
      unread: activeKey === "unread",
      append: page > 1,
    };

    if (activeKey === "unread") return payload;

    payload.timeFilter = timeFilter;

    const isCustomDate = selectedCustomFilter === "custom_date" && customDate;
    const isDateRange = selectedCustomFilter === "date_range";

    if (isCustomFilter) {
      payload.customFilter = selectedCustomFilter;
    }

    if (isCustomDate) {
      payload.customDate = formatToISO(customDate);
    }

    if (isDateRange) {
      if (customRange?.startDate)
        payload.startDate = formatToISO(customRange.startDate);
      if (customRange?.endDate)
        payload.endDate = formatToISO(customRange.endDate);
    }

    return payload;
  }, [
    page,
    activeKey,
    timeFilter,
    selectedCustomFilter,
    customDate,
    customRange,
    isCustomFilter,
  ]);

  const fetchAllNotification = useCallback(() => {
    const payload = buildNotificationPayload();
    dispatch(fetchNotifications(payload))
      .unwrap()
      .then((res) => {
        const { count, unread_count } = res.data;

        if (activeKey === "all" && count !== "undefined") {
          setAllCount(count);
        }

        if (unread_count !== "undefined") {
          setUnreadCount(unread_count);
        }
      })
      .catch(() => {
        const id = Date.now().toString();
        const newMsg = "Error fetching notifications";
        setNotificationError([{ id, newMsg }]);
        setShowNotification(true);
      });
  }, [dispatch, buildNotificationPayload, activeKey]);

  const handleTabChange = (key) => {
    setActiveKey(key);
    setPage(1);
  };

  const handleTimeFilterChange = (value) => {
    setTimeFilter(value);
    setPage(1);
  };

  const onNotificationClick = onNotificationClickFactory({
    dispatch,
    navigate,
    updateNotificationStatus,
    postHighlightedMatter,
    setNotificationError,
    setShowNotification,
  });

  useEffect(() => {
    if (isCustomCalendarOpen) return;
    if (isCustomFilter && activeKey !== "unread") return;
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    fetchAllNotification();
  }, [activeKey, timeFilter, page, isCustomFilter, isCustomCalendarOpen, fetchAllNotification]);

  useEffect(() => {
    if (isCustomFilter && apply) {
      fetchAllNotification();
    }
  }, [timeFilter, page, apply, isCustomFilter, fetchAllNotification]);

  useEffect(() => {
    if (apply) {
      const { startDate: labelStart, endDate: labelEnd } = getDateRange(
        selectedCustomFilter,
        customDate,
        customRange
      );
      setLabelRange({
        startDate: labelStart,
        endDate: labelEnd,
        sideMenuSelection: selectedCustomFilter,
      });
    }
  }, [apply, selectedCustomFilter, customDate, customRange]);

  // Fetch "all" count on page load so the All tab shows correct value when user opens the page
  useEffect(() => {
    fetchNotification({
      offset: 0,
      limit: 1,
      unread: false,
      timeFilter: "1m",
    })
      .then((res) => {
        if (res?.data?.count !== undefined) {
          setAllCount(res.data.count);
        }
      })
      .catch(() => {});
  }, []);

  // Refresh notifications when notification_update websocket message is received
  useEffect(() => {
    if (notificationRefreshTrigger > 0) {
      setPage(1);
      fetchNotification({
        offset: 0,
        limit: 1,
        unread: false,
        timeFilter: "1m",
      })
        .then((res) => {
          if (res?.data?.count !== undefined) {
            setAllCount(res.data.count);
          }
        })
        .catch(() => {});
    }
  }, [notificationRefreshTrigger]);

  const renderContent = () => {
    if (reloadPage) return <Loader />;
    if (flatListForRender.length === 0) {
      return (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <NoNotificationsIcon />

          <Box
            sx={{
              color: "#5A6C87",
              fontSize: "13px",
              lineHeight: "20px",
              fontWeight: 600,
              fontFamily: "Manrope",
            }}
          >
            {" "}
            No notifications
          </Box>
        </Box>
      );
    }
    return (
      <Virtuoso
        data={flatListForRender}
        overscan={200}
        style={{ height: "100%" }}
        endReached={() => {
          if (moreNotifications && !isLoading) {
            setPage((prev) => prev + 1);
          }
        }}
        components={{
          Footer: getVirtuosoFooter(isLoading, moreNotifications),
        }}
        itemContent={itemContent(flatListForRender, onNotificationClick)}
      />
    );
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
      }}
    >
      {/* Sticky header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#fff",
          padding: "32px 16px 0 16px",
        }}
      >
        <Box
          sx={{
            fontSize: "24px",
            fontWeight: 600,
            fontFamily: "Manrope",
          }}
        >
          {`Notifications (${unreadCount})`}
        </Box>
        {/* Tabs row - full width with border spanning entire row */}
        <Box
          sx={{
            paddingTop: 2,
            width: "100%",
          }}
        >
          <StyledTabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={[
              {
                key: "unread",
                label: `Unread (${unreadCount})`,
              },
              {
                key: "all",
                label: `All (${allCount})`,
              },
            ]}
          />
        </Box>
        {/* Filter row - Last Week (or first group) and filter side by side */}
        {activeKey !== "unread" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
              paddingBottom: 1,
              paddingRight: 3,
            }}
          >
            <Typography.Text type="secondary">
              {firstGroupLabel}
            </Typography.Text>
            <TimeFilterDropdown
              timeFilter={timeFilter}
              dateLabel={labelRange}
              handleTimeFilterChange={handleTimeFilterChange}
              selectedCustomFilter={selectedCustomFilter}
              setSelectedCustomFilter={setSelectedCustomFilter}
              customDate={customDate}
              setCustomDate={setCustomDate}
              customRange={customRange}
              setCustomRange={setCustomRange}
              appliedRange={appliedRange}
              setAppliedRange={setAppliedRange}
              setApply={setApply}
              onCustomCalendarOpenChange={setIsCustomCalendarOpen}
              onCustomCalendarCloseWithoutApply={() => {
                skipNextFetchRef.current = true;
              }}
              options={[
                { key: "1m", label: "Last 1 Month" },
                { key: "3m", label: "Last 3 Months" },
                { key: "custom", label: "Custom" },
              ]}
            />
          </Box>
        )}
      </Box>
      {/* Scrollable notifications */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: "0 16px",
        }}
      >
        {renderContent()}
      </Box>

      {showNotification && (
        <ErrorToast
          message={notificationError}
          onClose={(id) =>
            setNotificationError((prev) => prev.filter((msg) => msg.id !== id))
          }
        />
      )}
    </Box>
  );
};
